import { type AxiosInstance } from 'axios'

import { useGlobalConfigContext } from '../../../global-config'

import { apiBuildGlooClientSideAxiosInstance } from './apiBuildGlooClientSideAxiosInstance'

let glooClientCache: Map<unknown, unknown> | null = null

const getGlooClientCache = (): Map<unknown, unknown> => {
  glooClientCache ??= new Map()

  return glooClientCache
}

type ApiClientFactory<Client, Config = unknown> = (
  configuration?: Config,
  basePath?: string,
  axios?: AxiosInstance,
) => Client

/**
 * CLIENTSIDE HOOK
 *
 * This function retrieves Gloo API clients generated from
 * API Contracts (@redteclab/API) and caches them, so the same instance is reused.
 *
 * @example
 * ```
 * const apiClient = useGlooApi(SomeGeneratedControllerFactory)
 * apiClient.someMethod(...)
 * ```
 */
export const useGlooApi = <Client, Config = unknown>(
  apiClientFactory: ApiClientFactory<Client, Config>,
): Client => {
  const publicGlobalConfig = useGlobalConfigContext()

  const apiClientCache = getGlooClientCache()

  const inCache = apiClientCache.get(apiClientFactory) as Client | undefined

  if (inCache) {
    return inCache
  }

  const newInstance = apiClientFactory(
    undefined,
    publicGlobalConfig.glooUrl,
    apiBuildGlooClientSideAxiosInstance(publicGlobalConfig),
  )

  apiClientCache.set(apiClientFactory, newInstance)

  return newInstance
}
